import { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import Icon from 'components/elements/Icon'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'
import { useStopBodyScroll } from 'hooks/useScrollEffects'
import { useTransition } from 'hooks/useTransitions'

import styles from './modal.module.scss'

type Props = {
  trigger?: React.ReactNode
  open?: boolean
  onOpen?: () => void
  onClose?: () => void
  children?: React.ReactNode
  modalClassName?: string
  triggerClassName?: string
  closeButton?: boolean
}

const Modal = ({
  trigger = null,
  open = false,
  onOpen,
  onClose,
  children = null,
  modalClassName = '',
  triggerClassName = '',
  closeButton = false,
}: Props) => {
  const [internalOpen, setInternalOpen] = useState(open)
  const [closeClickStarted, setCloseClickStarted] = useState(false)

  const keylistener = useRef<(e: KeyboardEvent) => void>(() => null)

  const [overlayTransitionRef, overlayTransitionState] = useTransition(internalOpen)
  const [modalTransitionRef, modalTransitionState] = useTransition(internalOpen, {
    enteringClassName: styles.dropIn,
    exitingClassName: styles.liftOut,
  })

  const setNoScroll = useStopBodyScroll(internalOpen)

  // Allows the parent to control the modal if they so choose
  useEffect(() => {
    setInternalOpen(open)
  }, [open])

  useEffect(() => {
    setNoScroll(internalOpen)
  }, [internalOpen])

  // Escape keylistener setup & cleanup
  useEffect(() => {
    // If we open, add the escape listener
    if (internalOpen) {
      keylistener.current = (e: KeyboardEvent) => {
        if (basicKeybindSkips(e)) return

        if (e.key === 'Escape') handleClose(false)
      }

      window.addEventListener('keydown', keylistener.current)
    }
    // Remove the escape listener
    else window.removeEventListener('keydown', keylistener.current)

    return () => window.removeEventListener('keydown', keylistener.current)
  }, [internalOpen])

  // On open callback for parents who want it
  useEffect(() => {
    if (internalOpen && onOpen) onOpen()
  }, [internalOpen])

  const handleClose = (openState = false) => {
    if (onClose) onClose()

    setInternalOpen(openState)
  }

  // Using the built in onClick means that if you start the click in the modal and end it in the dimmer area it closes
  // the modal. This is frustrating when selecting text in an input field. To avoid this we track if the click started
  // in the dimmer area and only close the modal if it did and it also ended in the dimmer area.
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setCloseClickStarted(true)
    else setCloseClickStarted(false)
  }

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeClickStarted && e.target === e.currentTarget) handleClose(false)
    setCloseClickStarted(false)
  }

  return (
    <>
      {trigger && (
        <button className={triggerClassName} onClick={() => setInternalOpen(true)}>
          {trigger}
        </button>
      )}

      {overlayTransitionState !== 'hidden' &&
        ReactDOM.createPortal(
          <div
            // This is a hack, there's no way to style the mounting state of a component that's not mounted
            style={{ opacity: overlayTransitionState === 'mounting' ? 0 : undefined }}
            ref={overlayTransitionRef}
            className={styles.container}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}>
            {modalTransitionState !== 'hidden' && (
              <div ref={modalTransitionRef} className={`${styles.modal} ${modalClassName}`}>
                {closeButton && (
                  <button className={styles.closeButton} onClick={() => handleClose(false)}>
                    <Icon name="close" />
                  </button>
                )}
                {children}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
export default Modal
